<script setup lang="ts">
import { computed, h, onMounted, ref, watch, type VNode } from 'vue';
import { RouterLink, useRouter, type RouteLocationRaw } from 'vue-router';
import {
  NIcon,
  type DataTableColumns,
  type DataTableSortState,
  type DropdownOption,
  type PaginationInfo,
} from 'naive-ui';
import { IconDocumentText, IconMore, IconSearch } from '@/components/design/icons';
import { PlaybookPartyPosition, PlaybookStatus, type PlaybookInDb } from '@/api';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCreateButton from '@/components/design/base/GaiaCreateButton.vue';
import GaiaCountLabel from '@/components/design/base/GaiaCountLabel.vue';
import GaiaDropdown, { gaiaDangerOption } from '@/components/design/base/GaiaDropdown.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaFilterChip from '@/components/design/composite/GaiaFilterChip.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import { contractTypeNames, playbookPartyPositionMap } from '@/components/design/statusMaps';
import { useListQuerySync, usePaginationConfig } from '@/composables/useListQuerySync';
import { useRowDelete, type RowDeleteState } from '@/composables/useRowDelete';
import { usePlaybooksStore } from '@/stores/playbooksStore';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import { formatDateTimeForTable } from '@/utils/datetimeUtils';
import { extractErrorDetail } from '@/utils/errorHandler';
import GaiaCheckbox from '@/components/design/base/GaiaCheckbox.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { playbookRunsRoute, playbookEditRoute } from '@/router/links';

interface FilterOption {
  value: string;
  label: string;
}

// Column-filter keys, matching the fields the search endpoint filters on and
// the `?f_<key>=` params useListQuerySync mirrors them to.
const CONTRACT_TYPE_KEY = 'contract_type';
const POSITION_KEY = 'party_position';
// Selected values beyond this collapse into the chip's "+N" overflow label.
const MAX_CHIP_PILLS = 2;

const router = useRouter();
const message = useGaiaToast();
const store = usePlaybooksStore();
const contractTypeStore = useContractTypeStore();

const { searchQuery, currentPage, pageSize, sortKey, sortDirection, sortActivated, columnFilters } =
  useListQuerySync();

const typeMenuOpen = ref(false);
const positionMenuOpen = ref(false);

// The row the ⋯ menu is acting on: the strip that replaces it while a delete
// is confirmed, running, or has failed.
const rowDelete = useRowDelete<PlaybookInDb>();

async function loadPlaybooks(): Promise<void> {
  try {
    await store.fetchPlaybooks({
      text: searchQuery.value.trim(),
      page: currentPage.value,
      pageSize: pageSize.value,
      sortKey: sortActivated.value ? sortKey.value : undefined,
      sortDirection: sortActivated.value ? sortDirection.value : undefined,
      columnFilters: columnFilters.value,
    });
  } catch {
    // store.error drives the in-table error state; nothing else to do here.
  }
}

// Page 1 is reached either by fetching directly or by letting the page watcher
// fire — never both, so a filter change costs one request.
function reloadFromFirstPage(): void {
  if (currentPage.value === 1) {
    void loadPlaybooks();
  } else {
    currentPage.value = 1;
  }
}

watch([currentPage, pageSize], () => {
  void loadPlaybooks();
});

// Positions arrive from the server as raw strings, and a deployed build can lag
// the backend's enum — fall back to the raw value rather than rendering blank.
const positionLabels: Record<string, string | undefined> = playbookPartyPositionMap;

function positionLabel(value: string): string {
  return positionLabels[value] ?? value;
}

/* ---------------------------------------------------------------- filters */

// The server reports which values it actually holds; the generated enum is the
// fallback for a cold response.
function optionsFrom(
  key: string,
  fallback: string[],
  label: (value: string) => string
): FilterOption[] {
  const reported = store.filterOptions[key]?.filter(
    (value): value is string => typeof value === 'string' && value.length > 0
  );
  const values = reported?.length ? reported : fallback;
  return values.map(value => ({ value, label: label(value) }));
}

const contractTypeOptions = computed<FilterOption[]>(() =>
  optionsFrom(
    CONTRACT_TYPE_KEY,
    contractTypeStore.sortedContractTypes.map(type => type.name),
    value => value
  )
);

const positionOptions = computed<FilterOption[]>(() =>
  optionsFrom(POSITION_KEY, Object.values(PlaybookPartyPosition), positionLabel)
);

function selectedValues(key: string): string[] {
  return columnFilters.value[key] ?? [];
}

function isFilterValueSelected(key: string, value: string): boolean {
  return selectedValues(key).includes(value);
}

function writeFilter(key: string, values: string[]): void {
  const merged = { ...columnFilters.value };
  if (values.length > 0) {
    merged[key] = values;
  } else {
    delete merged[key];
  }
  columnFilters.value = merged;
  reloadFromFirstPage();
}

function toggleFilterValue(key: string, value: string, checked: boolean): void {
  const current = selectedValues(key);
  writeFilter(key, checked ? [...current, value] : current.filter(entry => entry !== value));
}

function selectedLabels(key: string, options: FilterOption[]): string[] {
  return selectedValues(key).map(
    value => options.find(option => option.value === value)?.label ?? value
  );
}

function chipValues(key: string, options: FilterOption[]): string[] {
  return selectedLabels(key, options).slice(0, MAX_CHIP_PILLS);
}

function chipOverflow(key: string, options: FilterOption[]): string | undefined {
  const hidden = selectedLabels(key, options).length - MAX_CHIP_PILLS;
  return hidden > 0 ? `+${hidden}` : undefined;
}

// The chip hands back the label it rendered, so map it to the stored value.
function removeChipValue(key: string, options: FilterOption[], label: string): void {
  const value = options.find(option => option.label === label)?.value ?? label;
  writeFilter(
    key,
    selectedValues(key).filter(entry => entry !== value)
  );
}

const filtersDirty = computed(
  () =>
    searchQuery.value.length > 0 ||
    selectedValues(CONTRACT_TYPE_KEY).length > 0 ||
    selectedValues(POSITION_KEY).length > 0
);

function resetFilters(): void {
  searchQuery.value = '';
  columnFilters.value = {};
  typeMenuOpen.value = false;
  positionMenuOpen.value = false;
  reloadFromFirstPage();
}

/* ------------------------------------------------------------ row actions */

function playbookRoute(playbook: PlaybookInDb): RouteLocationRaw {
  return playbookRunsRoute(playbook.uid);
}

function openPlaybook(playbook: PlaybookInDb): void {
  void router.push(playbookRoute(playbook));
}

function editPlaybook(playbook: PlaybookInDb): void {
  void router.push(playbookEditRoute(playbook.uid));
}

function openCreateView(): void {
  void router.push({ name: 'playbook-create' });
}

async function toggleArchived(playbook: PlaybookInDb): Promise<void> {
  const next =
    playbook.status === PlaybookStatus.ARCHIVED ? PlaybookStatus.DRAFT : PlaybookStatus.ARCHIVED;
  try {
    await store.setPlaybookStatus(playbook.uid, next);
    message.success(
      next === PlaybookStatus.ARCHIVED ? 'Playbook archived.' : 'Playbook restored to draft.'
    );
    await loadPlaybooks();
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to update the playbook.'));
  }
}

async function deletePlaybook(playbook: PlaybookInDb): Promise<void> {
  await rowDelete.run(playbook, {
    work: async () => {
      await store.deletePlaybook(playbook.uid);
      await loadPlaybooks();
      message.success(`Playbook "${playbook.name}" deleted.`);
    },
    errorText: error =>
      `Couldn't delete "${playbook.name}" — ${extractErrorDetail(error, 'the server rejected the request.')}`,
  });
}

// Opening and running are what the row itself does, so the menu carries only
// what the row cannot: editing, archiving, deleting.
function rowMenuOptions(playbook: PlaybookInDb): DropdownOption[] {
  return [
    { key: 'edit', label: 'Edit' },
    {
      key: 'archive',
      label: playbook.status === PlaybookStatus.ARCHIVED ? 'Restore to draft' : 'Archive',
    },
    { key: 'delete-divider', type: 'divider' },
    {
      key: 'delete',
      label: 'Delete playbook',
      props: gaiaDangerOption,
    },
  ];
}

function onRowMenuSelect(key: string, playbook: PlaybookInDb): void {
  if (key === 'edit') {
    editPlaybook(playbook);
    return;
  }
  if (key === 'archive') {
    void toggleArchived(playbook);
    return;
  }
  if (key === 'delete') {
    rowDelete.arm(playbook);
  }
}

/* ----------------------------------------------------------------- table */

// The state strip owns its own height, padding and hairline — the cell it
// spans must not add a second set.
const STATE_CELL_STYLE = { padding: '0', borderBottom: 'none' } as const;

function renderRowState(playbook: PlaybookInDb, state: Exclude<RowDeleteState, null>): VNode {
  if (state === 'busy') {
    return h(
      GaiaTableRowState,
      { variant: 'busy', note: 'Undo not available' },
      { default: () => `Deleting "${playbook.name}"…` }
    );
  }
  if (state === 'confirm') {
    return h(
      GaiaTableRowState,
      {
        variant: 'confirm',
        title: `Delete "${playbook.name}"?`,
        confirmLabel: 'Delete playbook',
        onConfirm: () => void deletePlaybook(playbook),
        onCancel: () => {
          rowDelete.cancel();
        },
      },
      {
        default: () =>
          'Reviews already run with it keep their results, but it can’t be used again.',
      }
    );
  }
  return h(
    GaiaTableRowState,
    {
      variant: 'error',
      onRetry: () => void deletePlaybook(playbook),
      onDismiss: () => {
        rowDelete.dismissError();
      },
    },
    { default: () => rowDelete.errorTextOf(playbook.uid) }
  );
}

// Clearing the sorter drops back to the server default without lighting up a
// header caret.
function handleSortChange(sorter: DataTableSortState | DataTableSortState[] | null): void {
  if (!sorter || Array.isArray(sorter)) return;
  if (sorter.order === false) {
    sortKey.value = 'created_at';
    sortDirection.value = 'desc';
    sortActivated.value = false;
  } else {
    sortActivated.value = true;
    sortKey.value = String(sorter.columnKey);
    sortDirection.value = sorter.order === 'ascend' ? 'asc' : 'desc';
  }
  reloadFromFirstPage();
}

function sortOrderFor(key: string): 'ascend' | 'descend' | false {
  if (!sortActivated.value || sortKey.value !== key) return false;
  return sortDirection.value === 'asc' ? 'ascend' : 'descend';
}

const columns = computed<DataTableColumns<PlaybookInDb>>(() => [
  {
    title: 'Name',
    key: 'name',
    sorter: true,
    sortOrder: sortOrderFor('name'),
    colSpan: (row): number => (rowDelete.stateOf(row.uid) ? 5 : 1),
    cellProps: (row): Record<string, unknown> =>
      rowDelete.stateOf(row.uid) ? { style: STATE_CELL_STYLE } : {},
    render: (row): VNode => {
      const state = rowDelete.stateOf(row.uid);
      if (state) return renderRowState(row, state);
      // A real link so cmd- and middle-click open the playbook in a new tab.
      return h(
        RouterLink,
        {
          to: playbookRoute(row),
          style: 'color: inherit; text-decoration: none; display: block; min-width: 0',
          onClick: (e: MouseEvent) => {
            e.stopPropagation();
          },
        },
        {
          default: () =>
            h(GaiaText, { weight: 'semibold', tone: 'ink', truncate: true }, () => row.name),
        }
      );
    },
  },
  {
    title: 'Contract type',
    key: 'contract_types',
    width: 190,
    ellipsis: { tooltip: true },
    render: (row): string => contractTypeNames(row.contract_types).join(', '),
  },
  {
    title: 'Position',
    key: 'party_position',
    width: 150,
    render: (row): string => positionLabel(row.party_position),
  },
  {
    title: 'Updated',
    key: 'updated_at',
    width: 190,
    sorter: true,
    sortOrder: sortOrderFor('updated_at'),
    render: (row): string => formatDateTimeForTable(row.updated_at ?? row.created_at),
  },
  {
    key: 'actions',
    title: '',
    width: 60,
    align: 'right',
    render: (row): VNode =>
      h(
        'div',
        {
          onClick: (event: MouseEvent): void => {
            event.stopPropagation();
          },
        },
        h(
          GaiaDropdown,
          {
            options: rowMenuOptions(row),
            placement: 'bottom-end',
            onSelect: (key: string): void => {
              onRowMenuSelect(key, row);
            },
          },
          {
            default: () =>
              h(
                GaiaIconButton,
                { dimension: 28, quaternary: true },
                { default: () => h(NIcon, { component: IconMore, size: 16 }) }
              ),
          }
        )
      ),
  },
]);

function rowProps(row: PlaybookInDb): Record<string, unknown> {
  if (rowDelete.stateOf(row.uid)) return {};
  return {
    style: 'cursor: pointer;',
    onClick: (): void => {
      openPlaybook(row);
    },
  };
}

const paginationConfig = usePaginationConfig(
  currentPage,
  pageSize,
  computed(() => store.totalCount)
);

const pagination = computed(() => ({
  ...paginationConfig.value,
  prefix: ({ startIndex, endIndex, itemCount }: PaginationInfo): string =>
    itemCount === 0 ? '0 of 0' : `${startIndex + 1}–${endIndex + 1} of ${itemCount}`,
}));

const countLabel = computed(() =>
  store.totalCount === 1 ? '1 playbook' : `${store.totalCount} playbooks`
);

const emptyVariant = computed<'error' | 'no-match' | 'none'>(() => {
  if (store.error) return 'error';
  return filtersDirty.value ? 'no-match' : 'none';
});

const noMatchTitle = computed(() =>
  searchQuery.value.trim()
    ? `No playbooks match “${searchQuery.value.trim()}”`
    : 'No playbooks match these filters'
);

function copyErrorDetails(): void {
  void navigator.clipboard.writeText(store.error ?? '');
  message.success('Error details copied.');
}

onMounted(() => {
  void contractTypeStore.ensureLoaded();
  void loadPlaybooks();
});
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.review }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.playbooks }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="14">
      <n-flex align="flex-start" justify="space-between" :size="16">
        <n-space align="baseline" :size="10">
          <gaia-header :level="1" variant="display" style="margin: 0">Playbooks</gaia-header>
          <gaia-count-label>{{ countLabel }}</gaia-count-label>
        </n-space>
        <gaia-create-button
          subject="playbook"
          data-tour="pb.create-button"
          @click="openCreateView"
        />
      </n-flex>

      <n-flex align="center" justify="space-between" :size="12">
        <n-flex align="center" :size="8">
          <gaia-popover
            trigger="manual"
            :raw="false"
            :show="typeMenuOpen"
            placement="bottom-start"
            @clickoutside="typeMenuOpen = false"
          >
            <template #trigger>
              <gaia-filter-chip
                field="Contract type"
                :active="typeMenuOpen"
                :values="chipValues(CONTRACT_TYPE_KEY, contractTypeOptions)"
                :overflow="chipOverflow(CONTRACT_TYPE_KEY, contractTypeOptions)"
                @toggle="typeMenuOpen = !typeMenuOpen"
                @remove="writeFilter(CONTRACT_TYPE_KEY, [])"
                @remove-value="
                  label => removeChipValue(CONTRACT_TYPE_KEY, contractTypeOptions, label)
                "
              />
            </template>
            <n-scrollbar style="max-height: 260px">
              <n-flex vertical :size="2">
                <gaia-checkbox
                  v-for="option in contractTypeOptions"
                  :key="option.value"
                  :checked="isFilterValueSelected(CONTRACT_TYPE_KEY, option.value)"
                  @update:checked="
                    (checked: boolean) =>
                      toggleFilterValue(CONTRACT_TYPE_KEY, option.value, checked)
                  "
                >
                  {{ option.label }}
                </gaia-checkbox>
              </n-flex>
            </n-scrollbar>
          </gaia-popover>

          <gaia-popover
            trigger="manual"
            :raw="false"
            :show="positionMenuOpen"
            placement="bottom-start"
            @clickoutside="positionMenuOpen = false"
          >
            <template #trigger>
              <gaia-filter-chip
                field="Position"
                :active="positionMenuOpen"
                :values="chipValues(POSITION_KEY, positionOptions)"
                :overflow="chipOverflow(POSITION_KEY, positionOptions)"
                @toggle="positionMenuOpen = !positionMenuOpen"
                @remove="writeFilter(POSITION_KEY, [])"
                @remove-value="label => removeChipValue(POSITION_KEY, positionOptions, label)"
              />
            </template>
            <n-flex vertical :size="2">
              <gaia-checkbox
                v-for="option in positionOptions"
                :key="option.value"
                :checked="isFilterValueSelected(POSITION_KEY, option.value)"
                @update:checked="
                  (checked: boolean) => toggleFilterValue(POSITION_KEY, option.value, checked)
                "
              >
                {{ option.label }}
              </gaia-checkbox>
            </n-flex>
          </gaia-popover>

          <gaia-link v-if="filtersDirty" @click="resetFilters">Reset</gaia-link>
        </n-flex>

        <gaia-search-input
          v-model:value="searchQuery"
          placeholder="Search playbooks"
          @search="reloadFromFirstPage"
        />
      </n-flex>

      <gaia-table
        data-tour="pb.list-table"
        :columns="columns"
        :data="store.playbooks"
        :loading="store.loading"
        :remote="true"
        :pagination="store.playbooks.length > 0 ? pagination : false"
        :row-key="(row: PlaybookInDb) => row.uid"
        :row-props="rowProps"
        @update:sorter="handleSortChange"
      >
        <template #empty>
          <gaia-empty-state
            v-if="emptyVariant === 'error'"
            status="alert"
            title="Couldn’t load playbooks"
          >
            {{ store.error }}
            <template #meta>playbook/search</template>
            <template #actions>
              <gaia-button size="small" type="primary" @click="loadPlaybooks">Retry</gaia-button>
              <gaia-button size="small" @click="copyErrorDetails">Copy error details</gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state v-else-if="emptyVariant === 'no-match'" :title="noMatchTitle">
            <template #icon><n-icon :component="IconSearch" :size="18" /></template>
            Search covers playbook names and contract types. Try a shorter term, or clear the search
            and filters to see all playbooks.
            <template #actions>
              <gaia-button size="small" @click="resetFilters">
                Clear search and filters
              </gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state v-else title="No playbooks yet">
            <template #icon><n-icon :component="IconDocumentText" :size="18" /></template>
            Upload a reference contract and Insights extracts its clauses into a playbook — every
            later contract of that type is checked against it.
            <template #actions>
              <gaia-create-button subject="playbook" :icon="false" @click="openCreateView" />
            </template>
          </gaia-empty-state>
        </template>
      </gaia-table>
    </n-flex>
  </Layout>
</template>
